import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { Badge } from "@/components/ui/badge"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { DatePicker } from "@/components/ui/date-picker"
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu"
import { Skeleton } from "@/components/ui/skeleton"
import { Search, Plus, ChevronLeft, ChevronRight, FileDown, CheckCircle2, XCircle } from "lucide-react"
import { useState, useEffect, useCallback, useMemo, useRef } from "react"
import { useShopStore } from "@/store/shop"
import { useAuthStore } from "@/store/auth"
import { useEmployeeStore } from "@/store/employee"
import { CreateEmployeeDialog } from "@/components/employee/create-employee-dialog"
import { getSettingExcel } from "@/api/setting"
import { downloadEmployeeExcel, getEmployeeTypeList } from "@/api/employee"
import { getShopCustomerGroup } from "@/api/shop"
import type { Employee, EmployeeListParameters, EmployeeType } from "@/types/employee"
import type { CustomerGroup } from "@/types/shop"
import { toast } from 'sonner'

type SearchType = "name" | "employeeId" | "email" | "phoneNumber" | "platformUserId"
type DateRangeType = "createdAt" | "updatedAt"

interface SearchFormState {
  searchType: SearchType
  searchKeyword: string
  dateRangeType: DateRangeType
  startDate?: Date
  endDate?: Date
  employeeTypeFilter: string
  pageSize: number
  currentPage: number
}

const INITIAL_FORM_STATE: SearchFormState = {
  searchType: "name",
  searchKeyword: "",
  dateRangeType: "createdAt",
  startDate: undefined,
  endDate: undefined,
  employeeTypeFilter: "all",
  pageSize: 50,
  currentPage: 1,
}

const SEARCH_TYPE_LABELS = {
  name: "이름",
  employeeId: "사번",
  email: "이메일",
  phoneNumber: "휴대폰번호",
  platformUserId: "회원 ID",
} as const

const PAGE_SIZE_OPTIONS = ["30", "50", "100", "200", "300", "500"] as const

export function EmployeeDeletePage() {
  // State
  const [formState, setFormState] = useState<SearchFormState>(INITIAL_FORM_STATE)

  // 초기화 상태 추적 (StrictMode 이중 호출 방지)
  const initializedShopNoRef = useRef<number | null>(null)
  
  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false)
  const [employeeTypes, setEmployeeTypes] = useState<EmployeeType[]>([])
  const [customerGroups, setCustomerGroups] = useState<CustomerGroup[]>([])
  
  // Store hooks
  const { selectedShop } = useShopStore()
  const { user } = useAuthStore()
  const { 
    employees, 
    totalCount,
    currentPage: storePage,
    totalPages,
    isLoading, 
    searchParams, 
    fetchEmployees, 
    resetSearchParams,
    setListType
  } = useEmployeeStore()
  
  // Computed values
  const isCafe24User = user?.platform === 'cafe24'
  const mallId = user?.platformMallId
  const shopNo = isCafe24User && selectedShop ? selectedShop.shopNo : 1
  const canFetchEmployees = !isCafe24User || !!selectedShop?.shopNo
  const isSuperAdmin = user?.isSuperAdmin;
  const allowedEmployeeTypeId = user?.allowedEmployeeTypeId;

  const customerGroupMap = useMemo(() => {
    const map = new Map<number, string>()
    customerGroups.forEach(group => {
      map.set(group.groupNo, group.groupName)
    })
    return map
  }, [customerGroups])

  // Utility functions
  const formatDateToKST = useCallback((date: Date): string => {
    const kstDate = new Date(date.getTime() + (9 * 60 * 60 * 1000))
    return kstDate.toISOString().split('T')[0]
  }, [])

  const formatPhoneNumber = useCallback((phoneNumber?: string): string => {
    if (!phoneNumber?.trim()) return '-'
    
    const numbers = phoneNumber.replace(/\D/g, '')
    if (numbers.length === 11) {
      return numbers.replace(/(\d{3})(\d{4})(\d{4})/, '$1-$2-$3')
    } else if (numbers.length === 10) {
      return numbers.replace(/(\d{3})(\d{3})(\d{4})/, '$1-$2-$3')
    }
    return phoneNumber
  }, [])

  const updateFormState = useCallback((updates: Partial<SearchFormState>) => {
    setFormState(prev => ({ ...prev, ...updates }))
  }, [])

  const fetchEmployeeMetadata = useCallback(async (targetShopNo: number) => {
    try {
      const [typeResponse, groupResponse] = await Promise.all([
        getEmployeeTypeList({ shopNo: targetShopNo }),
        getShopCustomerGroup({ shopNo: targetShopNo })
      ])

      if (typeResponse.code === 200 && typeResponse.data) {
        const fetchedTypes = typeResponse.data.types ?? []
        setEmployeeTypes(fetchedTypes)
        setFormState(prev => {
          if (prev.employeeTypeFilter === "all") return prev
          const selectedId = Number(prev.employeeTypeFilter)
          if (!Number.isNaN(selectedId) && fetchedTypes.some(type => type.typeId === selectedId)) {
            return prev
          }
          return { ...prev, employeeTypeFilter: "all" }
        })
      } else {
        setEmployeeTypes([])
        setFormState(prev => prev.employeeTypeFilter === "all" ? prev : { ...prev, employeeTypeFilter: "all" })
        if (typeResponse.message) {
          toast.error(typeResponse.message)
        } else {
          toast.error("임직원 구분을 불러오지 못했습니다.")
        }
      }

      if (groupResponse.code === 200 && groupResponse.data) {
        setCustomerGroups(groupResponse.data)
      } else {
        setCustomerGroups([])
        if (groupResponse.message) {
          toast.error(groupResponse.message)
        } else {
          toast.error("회원 등급 정보를 불러오지 못했습니다.")
        }
      }
    } catch (error) {
      console.error("임직원 메타데이터 조회 오류:", error)
      setEmployeeTypes([])
      setCustomerGroups([])
      setFormState(prev => prev.employeeTypeFilter === "all" ? prev : { ...prev, employeeTypeFilter: "all" })
      toast.error("임직원 메타데이터를 불러오는 중 오류가 발생했습니다.")
    }
  }, [])

  /**
   * 최초 리스트 불러오기
   */
  const initializeEmployeeData = useCallback(async (targetShopNo: number, forceReset = false) => {
    if (forceReset) {
      setFormState(INITIAL_FORM_STATE)
    }

    setListType('delete')

    const params: EmployeeListParameters = {
      shopNo: targetShopNo,
      page: 1,
      limit: INITIAL_FORM_STATE.pageSize,
    }
    // 슈퍼 어드민이 아니면 해당 구분만 조회한다
    if (!isSuperAdmin && allowedEmployeeTypeId !== undefined) {
      params.employeeTypeId = allowedEmployeeTypeId
    }

    resetSearchParams()
    await Promise.all([
      fetchEmployeeMetadata(targetShopNo),
      fetchEmployees(params)
    ])
  }, [isSuperAdmin, allowedEmployeeTypeId, resetSearchParams, fetchEmployeeMetadata, fetchEmployees, setListType])

  // Search functionality
  const buildSearchParams = useCallback(() => {
    const { searchType, searchKeyword, dateRangeType, startDate, endDate, employeeTypeFilter, pageSize } = formState
    
    const params: any = { shopNo, page: 1, limit: pageSize }

    // Keyword search
    if (searchKeyword.trim()) {
      params.searchType = searchType
      params.searchValue = searchType === "phoneNumber" 
        ? searchKeyword.trim().replace(/\D/g, '')
        : searchKeyword.trim()
    }

    // Date range search
    if (startDate) {
      params.dateType = dateRangeType
      params.startDate = formatDateToKST(startDate)
    }
    if (endDate) {
      params.dateType = dateRangeType
      params.endDate = formatDateToKST(endDate)
    }

    if (!isSuperAdmin) {
      params.employeeTypeId = allowedEmployeeTypeId
    } else {
      // Filters
      if (employeeTypeFilter !== "all") {
        const selectedTypeId = Number(employeeTypeFilter)
        if (!Number.isNaN(selectedTypeId)) {
          params.employeeTypeId = selectedTypeId
        }
      }
    }

    return params
  }, [formState, shopNo, formatDateToKST])

  const handleSearch = useCallback(async () => {
    const params = buildSearchParams()
    updateFormState({ currentPage: 1 })
    await fetchEmployees(params)
  }, [buildSearchParams, updateFormState, fetchEmployees])

  const handleReset = useCallback(async () => {
    setFormState(INITIAL_FORM_STATE)
    resetSearchParams()
    await fetchEmployees({ shopNo, page: 1, limit: INITIAL_FORM_STATE.pageSize })
  }, [resetSearchParams, fetchEmployees, shopNo])

  const handleCreateEmployee = useCallback(async () => {
    try {
      const response = await getSettingExcel({ shopNo, type: "upsert" })
      
      if (response.code === 200 && response.data) {
        setCreateDialogOpen(true)
      } else {
        toast.error(
          <div>
            임직원 엑셀 업로드 메뉴에서 <br />
            변경할 임직원 등급 관련 설정을 저장해 주세요. <br />
            해당 설정을 하셔야 임직원 등록시 회원 정보를 자동 변경합니다.
          </div>
        )
      }
    } catch (error) {
      console.error("엑셀 설정 조회 오류:", error)
    }
  }, [shopNo])

  // Excel download
  const handleExcelDownload = useCallback(async () => {
    try {
      const downloadParams = {
        ...buildSearchParams(),
        type: 'deleted',
        export: 'T'
      }

      toast.promise(
        downloadEmployeeExcel(downloadParams),
        {
          loading: '엑셀 파일을 생성하는 중입니다...',
          success: (blob) => {
            const url = window.URL.createObjectURL(blob)
            const link = document.createElement('a')
            link.href = url
            link.download = `임직원삭제목록_${new Date().toLocaleDateString('ko-KR').replace(/\./g, '')}.xlsx`
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
            window.URL.revokeObjectURL(url)
            
            return '엑셀 파일 다운로드가 완료되었습니다.'
          },
          error: '엑셀 다운로드 중 오류가 발생했습니다.'
        }
      )
    } catch (error) {
      console.error('엑셀 다운로드 오류:', error)
      toast.error('엑셀 다운로드 중 오류가 발생했습니다.')
    }
  }, [buildSearchParams])

  // Pagination
  const handlePageChange = useCallback(async (page: number) => {
    if (page >= 1 && page <= totalPages) {
      updateFormState({ currentPage: page })
      await fetchEmployees({ 
        ...searchParams,
        shopNo, 
        page, 
        limit: formState.pageSize 
      })
    }
  }, [totalPages, updateFormState, fetchEmployees, searchParams, shopNo, formState.pageSize])

  // Filter handlers
  const handleEmployeeTypeFilterChange = useCallback(async (value: string) => {
    updateFormState({ employeeTypeFilter: value, currentPage: 1 })
    
    const nextParams: Partial<EmployeeListParameters> = {
      ...searchParams,
      shopNo,
      page: 1,
      limit: formState.pageSize,
      employeeTypeId: undefined,
    }

    if (value === "all") {
      nextParams.employeeTypeId = undefined
    } else {
      const selectedTypeId = Number(value)
      nextParams.employeeTypeId = Number.isNaN(selectedTypeId) ? undefined : selectedTypeId
    }

    await fetchEmployees(nextParams)
  }, [updateFormState, fetchEmployees, searchParams, shopNo, formState.pageSize])

  const handlePageSizeChange = useCallback(async (value: string) => {
    const newPageSize = parseInt(value)
    updateFormState({ pageSize: newPageSize, currentPage: 1 })
    await fetchEmployees({ 
      ...searchParams,
      shopNo, 
      page: 1, 
      limit: newPageSize 
    })
  }, [updateFormState, fetchEmployees, searchParams, shopNo])

  // Effects
  useEffect(() => {
    updateFormState({ currentPage: storePage })
  }, [storePage, updateFormState])

  // shopNo 변경 시에만 데이터 초기화
  useEffect(() => {
    if (!canFetchEmployees) return

    // 같은 shopNo로 이미 초기화된 경우 스킵 (StrictMode 이중 호출 방지)
    if (initializedShopNoRef.current === shopNo) return

    initializedShopNoRef.current = shopNo
    initializeEmployeeData(shopNo, true)
  }, [canFetchEmployees, shopNo]) // initializeEmployeeData 제거 - 무한 루프 방지

  // Render helpers
  const renderLoadingSkeleton = useMemo(() => {
    const skeletonCount = Math.min(formState.pageSize, 10)
    return Array.from({ length: skeletonCount }, (_, index) => (
      <TableRow key={index}>
        <TableCell className="text-center"><Skeleton className="h-4 w-6 mx-auto" /></TableCell>
        <TableCell className="text-center"><Skeleton className="h-5 w-12 mx-auto rounded-full" /></TableCell>
        <TableCell className="text-center"><Skeleton className="h-4 w-16 mx-auto" /></TableCell>
        <TableCell className="text-center"><Skeleton className="h-4 w-12 mx-auto" /></TableCell>
        <TableCell className="text-center"><Skeleton className="h-4 w-16 mx-auto" /></TableCell>
        <TableCell className="text-center"><Skeleton className="h-4 w-28 mx-auto" /></TableCell>
        <TableCell className="text-center"><Skeleton className="h-4 w-40 mx-auto" /></TableCell>
        <TableCell className="text-center"><Skeleton className="h-4 w-20 mx-auto" /></TableCell>
        <TableCell className="text-center"><Skeleton className="h-4 w-20 mx-auto" /></TableCell>
        <TableCell className="text-center"><Skeleton className="h-4 w-20 mx-auto" /></TableCell>
        <TableCell className="text-center"><Skeleton className="h-4 w-3 mx-auto" /></TableCell>
      </TableRow>
    ))
  }, [formState.pageSize])

  const renderEmployeeRows = useMemo(() => {
    return employees.map((employee: Employee, index: number) => (
      <TableRow key={employee.id}>
        <TableCell className="text-center">
          {totalCount - ((formState.currentPage - 1) * formState.pageSize + index)}
        </TableCell>
        <TableCell className="text-center">
          <Badge variant={employee.employeeTypeId === 1 ? "warning" : "success"}>
            {employee.employeeTypeName?.trim()}
          </Badge>
        </TableCell>
        <TableCell className="text-center">
          {customerGroupMap.get(employee.lastRequestGroupNo) ?? "-"}
        </TableCell>
        <TableCell className="font-medium text-center">
          <span className="max-w-[120px] truncate inline-block align-middle" title={employee.employeeId || "-"}>
            {employee.employeeId || "-"}
          </span>
        </TableCell>
        <TableCell className="text-center">
          <span className="max-w-[120px] truncate inline-block align-middle" title={employee.name || "-"}>
            {employee.name || "-"}
          </span>
        </TableCell>
        <TableCell className="text-center">
          <span className="max-w-[140px] truncate inline-block align-middle" title={formatPhoneNumber(employee.phoneNumber)}>
            {formatPhoneNumber(employee.phoneNumber)}
          </span>
        </TableCell>
        <TableCell className="text-center">
          <span className="max-w-[180px] truncate inline-block align-middle" title={employee.email || "-"}>
            {employee.email || "-"}
          </span>
        </TableCell>
        <TableCell className="text-center">{employee.createdAt.split('T')[0]}</TableCell>
        <TableCell className="text-center">{employee.deletedAt ? employee.deletedAt?.split('T')[0] : "-"}</TableCell>
        <TableCell className="text-center">
          {(() => {
            const platformUserIds = employee.platformUserId
            
            if (!platformUserIds || platformUserIds.length === 0) {
              return "-"
            }
            
            if (platformUserIds.length === 1) {
              const userId = platformUserIds[0]
              if (mallId && employee.isLinked) {
                return (
                  <button
                    onClick={() => {
                      const url = `https://${mallId}.cafe24.com/admin/php/shop${shopNo}/c/member_admin_d_f.php?user_id=${userId.platformUserId}`
                      window.open(url, '_blank')
                    }}
                    className="text-foreground hover:text-blue-600 hover:underline cursor-pointer transition-colors max-w-32 truncate inline-block"
                    title={userId.platformUserId}
                  >
                    {userId.platformUserId}
                  </button>
                )
              }
              return (
                <span className="max-w-32 truncate inline-block" title={userId.platformUserId}>
                  {userId.platformUserId}
                </span>
              )
            }
            
            // 여러 개일 때 드롭다운 메뉴
            return (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button 
                    className="text-foreground hover:text-blue-600 hover:underline cursor-pointer transition-colors flex items-center justify-center max-w-32 mx-auto"
                    title={`${platformUserIds[0].platformUserId} 외 ${platformUserIds.length - 1}개`}
                  >
                    <span className="truncate min-w-0 flex-1">
                      {platformUserIds[0].platformUserId}
                    </span>
                    <span className="shrink-0 ml-1">
                      (+{platformUserIds.length - 1})
                    </span>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="center" className="min-w-48 max-w-64">
                  {platformUserIds.map((userId, index) => (
                    <DropdownMenuItem
                      key={userId.id}
                      className="cursor-pointer flex items-center justify-between gap-2 pr-2"
                      onClick={() => {
                        if (mallId && employee.isLinked) {
                          const url = `https://${mallId}.cafe24.com/admin/php/shop${shopNo}/c/member_admin_d_f.php?user_id=${userId.platformUserId}`
                          window.open(url, '_blank')
                        }
                      }}
                    >
                      <span className={`text-sm truncate flex-1 min-w-0 ${
                          userId.useLinked ? "text-foreground" : "text-muted-foreground"
                        }`} 
                        title={userId.platformUserId}>
                        {userId.platformUserId}
                      </span>
                      <div className="flex items-center gap-1">
                        {userId.useLinked ? (
                          <>
                            <CheckCircle2 className="h-3 w-3 text-green-600" />
                            <span className="text-xs text-green-600 font-medium">연동됨</span>
                          </>
                        ) : (
                          <>
                            <XCircle className="h-3 w-3 text-gray-400" />
                            <span className="text-xs text-gray-400">비연동</span>
                          </>
                        )}
                      </div>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            )
          })()}
        </TableCell>
      </TableRow>
    ))
  }, [employees, formState.currentPage, formState.pageSize, totalCount, formatPhoneNumber, mallId, shopNo, customerGroupMap])

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold tracking-tight">임직원 삭제 목록</h2>
          <p className="text-muted-foreground text-sm mt-3">
            퇴사/제휴 종료등으로 목록 수동 삭제, 엑셀 업로드 - 일반 회원 등급 일괄 변경 (퇴사 및 제휴 종료등)처리한 임직원 목록을 확인할 수 있습니다.
          </p>
        </div>
        <Button onClick={handleCreateEmployee}>
          <Plus className="h-4 w-4" />
          임직원 등록
        </Button>
      </div>

      {/* Search and Filter Card */}
      <Card>
        <CardHeader>
          <CardTitle>검색 및 필터</CardTitle>
          <CardDescription>
            이름, 사번, 휴대폰 번호, 쇼핑몰 회원 ID, 등록일, 수정일을 선택하여 검색할 수 있습니다.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex flex-col lg:flex-row gap-4 lg:items-end">
            {/* Date Search Group */}
            <div className="flex flex-col sm:flex-row gap-2 lg:gap-3 items-start lg:items-end">
              <Select 
                value={formState.dateRangeType} 
                onValueChange={(value) => updateFormState({ dateRangeType: value as DateRangeType })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="createdAt">등록일</SelectItem>
                  <SelectItem value="updatedAt">수정일</SelectItem>
                  <SelectItem value="deletedAt">삭제일</SelectItem>
                </SelectContent>
              </Select>
              
              <div className="w-full sm:w-44">
                <DatePicker
                  value={formState.startDate}
                  onChange={(date) => updateFormState({ startDate: date })}
                  placeholder="시작일 선택"
                />
              </div>
              
              <div className="w-full sm:w-44">
                <DatePicker
                  value={formState.endDate}
                  onChange={(date) => updateFormState({ endDate: date })}
                  placeholder="종료일 선택"
                />
              </div>
            </div>

            {/* Keyword Search Group */}
            <div className="flex flex-col sm:flex-row gap-2 flex-1 max-w-md">
              <Select 
                value={formState.searchType} 
                onValueChange={(value) => updateFormState({ searchType: value as SearchType })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="name">이름</SelectItem>
                  <SelectItem value="employeeId">사번</SelectItem>
                  <SelectItem value="email">이메일</SelectItem>
                  <SelectItem value="phoneNumber">휴대폰번호</SelectItem>
                  <SelectItem value="platformUserId">회원 ID</SelectItem>
                </SelectContent>
              </Select>
              
              <div className="relative flex-1">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder={`${SEARCH_TYPE_LABELS[formState.searchType]}으로 검색...`}
                  className="pl-8"
                  value={formState.searchKeyword}
                  onChange={(e) => updateFormState({ searchKeyword: e.target.value })}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault()
                      handleSearch()
                    }
                  }}
                />
              </div>
            </div>
            
            {/* Search Buttons */}
            <div className="flex gap-2">
              <Button onClick={handleSearch} disabled={isLoading}>
                <Search className="h-4 w-4" />
                검색
              </Button>
              <Button variant="outline" onClick={handleReset} disabled={isLoading}>
                초기화
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Employee List Card */}
      <Card className="gap-3">
        <CardHeader>
          <CardTitle>임직원 목록</CardTitle>
          <CardDescription>
            총 {totalCount}명이 삭제되어 있습니다.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* Top Actions */}
          <div className="flex items-center justify-end mb-4">
            <div className="flex items-center gap-3">
              {!isLoading && employees.length > 0 && (
                <Button variant="outline" onClick={handleExcelDownload}>
                  <FileDown className="h-4 w-4" />
                  엑셀다운로드
                </Button>
              )}
              {isSuperAdmin && (
                <>
                  <Select
                    key={`employee-type-filter-${employeeTypes.map(type => type.typeId).join("-")}`}
                    value={formState.employeeTypeFilter}
                    onValueChange={handleEmployeeTypeFilterChange}
                  >
                    <SelectTrigger className="w-36">
                      <SelectValue placeholder="구분 전체" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">구분 전체</SelectItem>
                      {employeeTypes.length === 0 ? (
                        <SelectItem value="no-types" disabled>
                          등록된 구분이 없습니다.
                        </SelectItem>
                      ) : (
                        employeeTypes.map(type => (
                          <SelectItem key={type.typeId} value={type.typeId.toString()}>
                            {type.typeName}
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                </>
              )}

              <Select value={formState.pageSize.toString()} onValueChange={handlePageSizeChange}>
                <SelectTrigger className="w-33">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PAGE_SIZE_OPTIONS.map(size => (
                    <SelectItem key={size} value={size}>{size}개씩 보기</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          
          {/* Data Table */}
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-center">번호</TableHead>
                  <TableHead className="text-center">구분</TableHead>
                  <TableHead className="text-center">회원 등급</TableHead>
                  <TableHead className="text-center">사번</TableHead>
                  <TableHead className="text-center">이름</TableHead>
                  <TableHead className="text-center">휴대폰번호</TableHead>
                  <TableHead className="text-center">이메일</TableHead>
                  <TableHead className="text-center">등록일</TableHead>
                  <TableHead className="text-center">삭제일</TableHead>
                  <TableHead className="text-center">회원 ID</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? renderLoadingSkeleton : (
                  employees.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={11} className="text-center py-8 text-muted-foreground">
                        등록된 직원이 없습니다.
                      </TableCell>
                    </TableRow>
                  ) : renderEmployeeRows
                )}
              </TableBody>
            </Table>
          </div>
          
          {/* Bottom Actions and Pagination */}
          <div className="flex items-center justify-end mt-4">
            {totalPages > 1 && (
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(formState.currentPage - 1)}
                  disabled={formState.currentPage === 1 || isLoading}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="text-sm text-muted-foreground">
                  {formState.currentPage} / {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(formState.currentPage + 1)}
                  disabled={formState.currentPage === totalPages || isLoading}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Dialogs */}
      <CreateEmployeeDialog 
        open={createDialogOpen} 
        onOpenChange={setCreateDialogOpen} 
      />
      
    </div>
  )
}
